import React from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import { ProjectStatus, ProjectSynthesisJobType } from '@Pages/RetroSynthesis/constants';
import { isPredictiveRetrosynthesisEnabled } from '@Pages/RetroSynthesis/predictiveUtils';
import Button from '@Components/Button';
import NoResults from './NoResults';
import RoutesStatus from './RoutesStatus';

import './ProjectStatusGridItem.theme.scss';

const isResultless = (status, count) => !status || status === ProjectStatus.NO_RESULTS || (status === ProjectStatus.FINISHED && !count);

const isPredictedStatusVisible = (predictedStatus, predictedRoutesCount) =>
  isPredictiveRetrosynthesisEnabled() &&
  !isEmpty(predictedStatus) &&
  predictedStatus !== ProjectStatus.CLEANED &&
  !(predictedStatus === ProjectStatus.FINISHED && isNil(predictedRoutesCount));

const isExtendedLastStepNoResults = (predictedStatus, predictedRoutesCount, step) =>
  (predictedStatus === ProjectStatus.FINISHED || predictedStatus === ProjectStatus.NO_RESULTS) && step === 2 && !predictedRoutesCount;

const isViewResultsVisible = (
  publishedStatus,
  publishedRoutesCount,
  predictedStatus,
  predictedRoutesCount,
  showCustomized,
  customizedRouteCount
) =>
  (publishedStatus === ProjectStatus.FINISHED && publishedRoutesCount > 0) ||
  (predictedStatus === ProjectStatus.FINISHED && predictedRoutesCount > 0) ||
  (showCustomized && customizedRouteCount > 0);

function ProjectStatusGridItem({
  projectId,
  publishedStatus,
  predictedStatus,
  publishedRoutesCount,
  predictedRoutesCount,
  customizedRouteCount,
  onInteraction,
  openSynthesisJobRetryDialog,
  onResults,
  onEdit,
  rtErrorReason,
  onRerun,
  queuePosition,
  step,
  partner,
}) {
  const showCustomized = !isNil(customizedRouteCount);
  if (
    !isPredictiveRetrosynthesisEnabled() &&
    isResultless(publishedStatus, publishedRoutesCount) &&
    !(showCustomized && customizedRouteCount > 0)
  ) {
    return <NoResults />;
  }
  const showPublishedStatus =
    !isNil(publishedStatus) &&
    publishedStatus !== ProjectStatus.CLEANED &&
    !(publishedStatus === ProjectStatus.FINISHED && isNil(publishedRoutesCount));
  const showPredictedStatus = isPredictedStatusVisible(predictedStatus, predictedRoutesCount);
  const canViewResults = isViewResultsVisible(
    publishedStatus,
    publishedRoutesCount,
    predictedStatus,
    predictedRoutesCount,
    showCustomized,
    customizedRouteCount
  );
  const rerunEnabled = publishedStatus === ProjectStatus.CLEANED || predictedStatus === ProjectStatus.CLEANED;
  const extLastStepNoResults = isExtendedLastStepNoResults(predictedStatus, predictedRoutesCount, step);
  const types = [];
  if (!isNil(publishedStatus) && publishedStatus === ProjectStatus.CLEANED) {
    types.push(ProjectSynthesisJobType.PUBLISHED);
  }
  if (!isNil(predictedStatus) && predictedStatus === ProjectStatus.CLEANED) {
    types.push(ProjectSynthesisJobType.PREDICTED);
  }
  return (
    <div className="project-status-grid-item">
      {showCustomized && (
        <RoutesStatus
          type={ProjectSynthesisJobType.CUSTOMIZED}
          status={ProjectStatus.FINISHED}
          label="Customized"
          routesCount={customizedRouteCount}
        />
      )}
      {showPredictedStatus && (
        <RoutesStatus
          type={ProjectSynthesisJobType.PREDICTED}
          status={predictedStatus}
          label="Predicted"
          routesCount={predictedRoutesCount}
          openSynthesisJobRetryDialog={openSynthesisJobRetryDialog}
          errorReason={rtErrorReason}
          queuePosition={queuePosition}
          step={step}
          partner={partner}
        />
      )}
      {showPublishedStatus && (
        <RoutesStatus
          type={ProjectSynthesisJobType.PUBLISHED}
          status={publishedStatus}
          label="Published"
          routesCount={publishedRoutesCount}
          onSelectAlternativeTarget={onInteraction}
          openSynthesisJobRetryDialog={openSynthesisJobRetryDialog}
          errorReason={rtErrorReason}
        />
      )}
      {rerunEnabled && (
        <>
          <RoutesStatus status={ProjectStatus.CLEANED} />
          <Button
            buttonConfig={{
              label: 'Rerun',
              e2eName: 'rerun-results',
              size: 'sm',
              type: 'primary',
              className: 'project-status-grid-item__view-results-button',
            }}
            iconConfig={{
              name: 'chevron-right',
            }}
            onClick={() => onRerun(types)}
          />
        </>
      )}
      {!rerunEnabled && extLastStepNoResults && (
        <>
          <div className="project-status-grid-item__edit-project-label">
            Retry your project with a simplified molecule, delete counterion, eliminate metal atom, and consider tautomers
          </div>
          <Button
            buttonConfig={{
              label: 'Edit',
              e2eName: 'edit-project',
              size: 'sm',
              type: 'primary',
              className: 'project-status-grid-item__edit-button',
            }}
            iconConfig={{
              name: 'pencil',
            }}
            onClick={onEdit}
          />
        </>
      )}
      {!rerunEnabled && !extLastStepNoResults && (
        <Button
          buttonConfig={{
            label: 'View',
            e2eName: 'view-results',
            size: 'sm',
            type: 'primary',
            className: 'project-status-grid-item__view-results-button',
          }}
          iconConfig={{
            name: 'chevron-right',
          }}
          onClick={onResults}
          id={`project-status__view-results-button-${projectId}`}
          disabled={!canViewResults}
        />
      )}
    </div>
  );
}

ProjectStatusGridItem.defaultProps = {
  onInteraction: () => null,
  openSynthesisJobRetryDialog: () => null,
  onResults: () => null,
};

ProjectStatusGridItem.propTypes = {
  projectId: PropTypes.number,
  publishedStatus: PropTypes.oneOf(ProjectStatus.allTypes),
  predictedStatus: PropTypes.oneOf(ProjectStatus.allTypes),
  publishedRoutesCount: PropTypes.number,
  predictedRoutesCount: PropTypes.number,
  customizedRouteCount: PropTypes.number,
  onInteraction: PropTypes.func,
  openSynthesisJobRetryDialog: PropTypes.func,
  onResults: PropTypes.func,
  onEdit: PropTypes.func,
  rtErrorReason: PropTypes.string,
  onRerun: PropTypes.func,
  queuePosition: PropTypes.string,
  step: PropTypes.number,
  partner: PropTypes.string,
};

export default ProjectStatusGridItem;
